<template>
  <div class="custom-tree-view">
    <CustomTreeNode
      ref="rootRefs"
      v-for="node in treeData"
      :key="node.id"
      :node="node"
      :fields="fields"
      :selectedNodes="modelValue"
      :disabledNodes="disabledNodes"
      @check-change="handleCheckChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ref } from 'vue';
import CustomTreeNode from './CustomTreeNode.vue';

interface TreeNode {
  id: string;
  pid?: string;
  text: string;
  hasChild?: boolean;
  children?: TreeNode[];
}

export default defineComponent({
  name: 'CustomTreeView',
  components: {
    CustomTreeNode
  },
  props: {
    dataSource: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    modelValue: { // v-model for selectedNodes
      type: Array as PropType<any[]>,
      default: () => []
    },
    disabledNodes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    fields: {
      type: Object,
      default: () => ({ id: 'id', pid: 'pid', text: 'text', hasChildren: 'hasChild' })
    }
  },
  emits: ['update:modelValue', 'node-check'],
  setup(props, { emit }) {

    // Convert flat list to tree
    const treeData = computed(() => {
      const data = JSON.parse(JSON.stringify(props.dataSource)); // Deep copy
      const map: { [key: string]: TreeNode } = {};
      const roots: TreeNode[] = [];

      // First pass: map nodes
      data.forEach((node: any) => {
        node.children = [];
        // flexible id mapping
        const id = node[props.fields.id];
        map[id] = node;
      });

      // Second pass: link parents
      data.forEach((node: any) => {
        const id = node[props.fields.id];
        const pid = node[props.fields.pid];
        
        if (pid && map[pid]) {
          map[pid].children?.push(node);
        } else {
          roots.push(node);
        }
      });

      return roots;
    });

    const getAllDescendantIds = (node: TreeNode): string[] => {
      let ids: string[] = [];
      if (node.children) {
        node.children.forEach(child => {
          ids.push(child.id);
          ids = ids.concat(getAllDescendantIds(child));
        });
      }
      return ids;
    };
    
    const getParentId = (id: string): string | undefined => {
       const node = props.dataSource.find((n: any) => n.id === id);
       return node ? node.pid : undefined;
    };

    const getSiblings = (id: string): string[] => {
        const pid = getParentId(id);
        if (!pid) {
            // Root nodes siblings?
            return props.dataSource.filter((n: any) => !n.pid).map((n: any) => n.id);
        }
        return props.dataSource.filter((n: any) => n.pid === pid).map((n: any) => n.id);
    };

    const handleCheckChange = ({ node, checked }: { node: TreeNode, checked: boolean }) => {
      // Emit event for validation cancellation (if needed, though naive implementation just updates)
      // Note: syncfusion event was cancelable. Here we can check before updating.
      // But props are passed down, so we just emit new value.
      
      // We should check if we should cancel.
      // But parent usage is: if (args.action == 'uncheck' && args.node.dataset.uid == 'settings') args.cancel = true;
      
      // Let's emit a specific event that looks like Syncfusion's args for compatibility or ease of migration
      const args = {
        action: checked ? 'check' : 'uncheck',
        node: { dataset: { uid: node.id } }, // Mock object for compatibility
        cancel: false
      };
      
      emit('node-check', args);
      
      if (args.cancel) return;

      let newSelected = [...props.modelValue];

      const descendants = getAllDescendantIds(node);
      const selfAndDescendants = [node.id, ...descendants];

      if (checked) {
        // Add self and descendants
        selfAndDescendants.forEach(id => {
          if (!newSelected.includes(id)) newSelected.push(id);
        });

        // Check parent?
        // If all siblings are checked, check parent.
        let currentId = node.id;
        while(true) {
            const pid = getParentId(currentId);
            if (!pid) break;
            
            const siblings = getSiblings(currentId);
            const allSiblingsChecked = siblings.every(sibId => newSelected.includes(sibId));
            
            if (allSiblingsChecked) {
                if(!newSelected.includes(pid)) newSelected.push(pid);
                currentId = pid;
            } else {
                break;
            }
        }
      } else {
        // Remove self and descendants
        newSelected = newSelected.filter(id => !selfAndDescendants.includes(id));

        // Uncheck parent(s)
        let currentId = node.id;
        while(true) {
            const pid = getParentId(currentId);
            if (!pid) break;
            
            // If any child is unchecked, parent must be unchecked
            if (newSelected.includes(pid)) {
                newSelected = newSelected.filter(id => id !== pid);
                currentId = pid;
            } else {
                break;
            }
        }
      }

      emit('update:modelValue', newSelected);
    };

    const checkAll = () => {
        const allIds = props.dataSource.map((n: any) => n[props.fields.id]);
        emit('update:modelValue', allIds);
    };

    const uncheckAll = () => {
        emit('update:modelValue', []);
    };
    
    const getAllCheckedNodes = () => {
        return props.modelValue;
    };

    const rootRefs = ref<any[]>([]); // Need to capture v-for refs?
    // Vue 3 v-for refs are arrays if used inside the render function/template automatically if variable name matches?
    // <CustomTreeNode ref="rootRefs">
    
    // Actually, template just uses <CustomTreeNode>. We need to add ref="rootRefs" to it.
    
    const expandAll = () => {
        setAllExpanded(true);
    };

    const collapseAll = () => {
        setAllExpanded(false);
    };

    const setAllExpanded = (expanded: boolean) => {
        // Access children refs. We need to modify template to bind ref="rootRefs"
        if (rootRefs.value) {
           rootRefs.value.forEach((node: any) => {
               if(node.setExpanded) node.setExpanded(expanded);
           });
        }
    };

    return {
      treeData,
      handleCheckChange,
      checkAll,
      uncheckAll,
      expandAll,
      collapseAll,
      getAllCheckedNodes,
      rootRefs
    };
  }
});
</script>

